import React, { useEffect, useMemo } from 'react'
import { useUser } from 'src/Hooks/useUser'
import { Skeleton } from '@mui/material'
import { isUndefined } from 'lodash'
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { useDispatch } from 'react-redux'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TabsBarTab } from '@e3dc-react/shell/Elements/Tabs/TabsBar'
import CompanyPermissions from 'src/Libs/company-settings/components/CompanyPermissions'
import CompanyAddressEditor from 'src/Libs/company-settings/components/CompanyAddressEditor'
import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'

/**
 * The main page for displaying the company settings.
 *
 * Manages an intermediate loader as long as the required dependencies for the profile editor are not available.
 * Also executes a redirect if the current authenticated user does not match as the manager of the requested company.
 *
 * Restrictions:
 * - only managers are allowed to see this page
 *
 * TODO: fetch the company information here directly, because the currently used information are not dependable as they are fetched implicit during the application runtime
 *
 * @constructor
 */
const CompanyPage: React.FC = () => {
  const { userCompany, userData } = useUser()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, selectedAppLanguage } = useCustomTranslation()
  const { setTabs, setActiveTab } = useHeaderTabs()
  const { pathname } = useLocation()

  const tabs: Array<TabsBarTab> = useMemo(() => {
    const tabs: TabsBarTab[] = [
      {
        label: t('administration.companyManagement.permissions.tab'),
        href: '/company/permissions',
        component: userCompany ? <CompanyPermissions companyId={userCompany.id} /> : <></>,
        componentHref: '/permissions',
      },
      {
        label: t('administration.companyManagement.companyProfile.tab'),
        href: '/company/profile',
        component: <CompanyAddressEditor />,
        componentHref: '/profile',
      },
    ]
    return tabs
  }, [t, userCompany])

  useEffect(() => {
    dispatch(appUpdate({ title: t('administration.companyManagement.title') }))
    const activeTabIndex = tabs.findIndex(({ href }) => href === pathname)
    setTabs(tabs)
    setActiveTab(activeTabIndex === -1 ? 0 : activeTabIndex)

    return () => {
      setTabs([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppLanguage, pathname])

  if (isUndefined(userCompany) || isUndefined(userData)) {
    return <Skeleton />
  }

  if (userCompany?.Manager !== userData?.ID) {
    navigate('/', { replace: true })
  }

  return (
    <Routes>
      {tabs.map(tab => (
        <Route key={tab.href} path={tab.componentHref} element={tab.component} />
      ))}
    </Routes>
  )
}

export default CompanyPage
